import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { useAuth } from '../context/AuthContext';
import { studentAPI } from '../utils/api';

const STATUS_CONFIG = {
  not_started: { color: 'var(--red)', bg: 'var(--red-light)', label: 'Not Started', action: 'Start ›' },
  in_progress: { color: '#B45309', bg: 'var(--yellow-light)', label: 'In Progress', action: 'Continue ›' },
  revision_due: { color: 'var(--blue)', bg: 'var(--blue-light)', label: 'Revision Due', action: 'Revise Now' },
  overdue: { color: 'var(--red)', bg: 'var(--red-light)', label: '⚠ Overdue', action: 'Revise Now' },
  mastered: { color: 'var(--green)', bg: 'var(--green-light)', label: '✓ Mastered', action: null }
};

const SUBJECT_ICONS = {
  'mathematics': '✏️', 'science': '⚙️', 'social_science': '🌍',
  'english': '📖', 'hindi': '🔤', 'sanskrit': '📜', 'ai_ct': '🧠', 'art_education': '🎨'
};

const SubjectsPage = () => {
  const { subjectId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [activeSubject, setActiveSubject] = useState(subjectId || null);
  const [chapters, setChapters] = useState([]);
  const [subjectInfo, setSubjectInfo] = useState(null);
  const [expandedChapters, setExpandedChapters] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => { loadSubjects(); }, []);
  useEffect(() => { if (activeSubject) loadChapters(activeSubject); }, [activeSubject]);

  const loadSubjects = async () => {
    try {
      const res = await studentAPI.getSubjects();
      const subs = res.data.data;
      setSubjects(subs);
      if (!activeSubject && subs.length > 0) setActiveSubject(subs[0].id);
    } catch { setSubjects([]); } finally { setLoading(false); }
  };

  const loadChapters = async (sId) => {
    setLoading(true);
    try {
      const res = await studentAPI.getChapters(sId);
      setChapters(res.data.data.chapters || []);
      setSubjectInfo(res.data.data.subject);
      const expanded = {};
      (res.data.data.chapters || []).forEach(ch => { expanded[ch.id] = true; });
      setExpandedChapters(expanded);
    } catch { setChapters([]); } finally { setLoading(false); }
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await studentAPI.search(activeSubject, q);
      setSearchResults(res.data.data);
    } catch { setSearchResults([]); }
  };

  const getChapterCompletion = (ch) => {
    if (!ch.topics?.length) return { done: 0, total: 0, pct: 0 };
    const done = ch.topics.filter(t => t.status === 'mastered').length;
    return { done, total: ch.topics.length, pct: Math.round((done / ch.topics.length) * 100) };
  };

  const activeSubjectData = subjects.find(s => s.id === activeSubject);
  const masteredCount = chapters.reduce((a, ch) => a + ch.topics.filter(t => t.status === 'mastered').length, 0);
  const totalTopics = chapters.reduce((a, ch) => a + ch.topics.length, 0);

  return (
    <AppShell>
      <div className="topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--muted)' }}>
          <span style={{ color: 'var(--text)', fontWeight: 700 }}>My Subjects</span>
          {activeSubjectData && <><span style={{ color: 'var(--border)' }}>›</span><span style={{ color: 'var(--brand-primary)' }}>{activeSubjectData.name}</span></>}
        </div>
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '20px', padding: '5px 12px', fontSize: '12px', color: 'var(--muted)', width: '220px' }}>
            <span>🔍</span>
            <input style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '12px', color: 'var(--text)', width: '100%' }}
              placeholder={`Search in ${activeSubjectData?.name || 'subjects'}...`}
              value={searchQuery} onChange={e => handleSearch(e.target.value)} />
          </div>
          {searchResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', right: 0, width: '280px', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginTop: '4px', zIndex: 100, maxHeight: '200px', overflowY: 'auto' }}>
              {searchResults.map((r, i) => (
                <div key={i} style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid var(--bg)', fontSize: '12px' }}
                  onClick={() => { navigate(r.type === 'topic' ? `/study/${r.id}` : `/subjects/${activeSubject}`); setSearchResults([]); setSearchQuery(''); }}>
                  <span style={{ fontSize: '10px', background: r.type === 'topic' ? 'var(--blue-light)' : 'var(--yellow-light)', color: r.type === 'topic' ? 'var(--blue)' : '#B45309', borderRadius: '20px', padding: '1px 6px', marginRight: '6px', fontWeight: 800 }}>{r.type}</span>
                  <strong>{r.name}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

        <div className="fade-up-1" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {subjects.map(s => (
            <button key={s.id} onClick={() => { setActiveSubject(s.id); navigate(`/subjects/${s.id}`); }}
              style={{ padding: '7px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, border: activeSubject === s.id ? 'none' : '1.5px solid var(--border)', background: activeSubject === s.id ? 'var(--brand-primary)' : 'var(--white)', color: activeSubject === s.id ? 'white' : 'var(--muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '14px' }}>{SUBJECT_ICONS[s.id] || '📚'}</span>
              {s.name}
              <span style={{ fontSize: '10px', fontWeight: 800, padding: '1px 5px', borderRadius: '10px', background: activeSubject === s.id ? 'rgba(255,255,255,0.25)' : 'var(--brand-secondary-light)', color: activeSubject === s.id ? 'white' : 'var(--brand-secondary)' }}>{s.topicsCount || 0}</span>
            </button>
          ))}
        </div>

        {activeSubjectData && (
          <div className="card fade-up-2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--brand-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>{SUBJECT_ICONS[activeSubject] || '📚'}</div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 800 }}>{activeSubjectData.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>Class {user?.classId} · CBSE · {chapters.length} Chapters · {totalTopics} Topics</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              {[{ val: masteredCount, lbl: 'Mastered' }, { val: totalTopics - masteredCount, lbl: 'Remaining' }].map((s, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 800 }}>{s.val}</div>
                  <div style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 600 }}>{s.lbl}</div>
                </div>
              ))}
              <div style={{ width: '1px', height: '36px', background: 'var(--border)' }}></div>
              <div style={{ width: '120px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--muted)', marginBottom: '4px', fontWeight: 600 }}>
                  <span>Mastery Score</span><span style={{ color: 'var(--brand-primary)', fontWeight: 800 }}>{activeSubjectData.mastery}/100</span>
                </div>
                <div style={{ background: 'var(--border)', borderRadius: '10px', height: '7px' }}>
                  <div style={{ background: 'var(--brand-primary)', borderRadius: '10px', height: '100%', width: `${activeSubjectData.mastery}%` }}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="fade-up-2" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--muted)', fontWeight: 600 }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: cfg.color }}></div>
              {cfg.label}
            </div>
          ))}
        </div>

        <div className="fade-up-3" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
          ) : chapters.length === 0 ? (
            <div className="card empty-state">
              <div className="empty-state-icon">📚</div>
              <div className="empty-state-text">Syllabus coming soon</div>
              <div className="empty-state-sub">Content is being prepared. Check back soon!</div>
            </div>
          ) : chapters.map((ch, idx) => {
            const { done, total, pct } = getChapterCompletion(ch);
            const isExpanded = expandedChapters[ch.id];
            const allDone = done === total && total > 0;
            return (
              <div key={ch.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                  onClick={() => setExpandedChapters(p => ({ ...p, [ch.id]: !p[ch.id] }))}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--brand-secondary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: 'var(--brand-secondary)', flexShrink: 0 }}>{idx + 1}</div>
                  <div style={{ flex: 1, fontSize: '13px', fontWeight: 800 }}>{ch.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 700 }}>{done}/{total}</span>
                    <div style={{ width: '60px', background: 'var(--border)', borderRadius: '10px', height: '5px' }}>
                      <div style={{ background: allDone ? 'var(--green)' : 'var(--brand-primary)', borderRadius: '10px', height: '100%', width: `${pct}%` }}></div>
                    </div>
                    {allDone ? <span style={{ fontSize: '10px', fontWeight: 800, color: 'var(--green)' }}>✓ Mastered</span>
                      : <span style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 700 }}>{pct}%</span>}
                  </div>
                  <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{isExpanded ? '▲' : '▼'}</span>
                </div>
                {isExpanded && (
                  <div style={{ padding: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {ch.topics.map(topic => {
                      const cfg = STATUS_CONFIG[topic.status] || STATUS_CONFIG.not_started;
                      return (
                        <div key={topic.id}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--white)', transition: 'all 0.15s' }}
                          onClick={() => navigate(`/study/${topic.id}`)}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; e.currentTarget.style.background = 'var(--brand-primary-light)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--white)'; }}>
                          <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: cfg.color, flexShrink: 0 }}></div>
                          <div style={{ flex: 1, fontSize: '12px', fontWeight: 700 }}>{topic.name}</div>
                          <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 8px', borderRadius: '20px', background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                          {cfg.action && <span style={{ fontSize: '10px', fontWeight: 800, color: 'var(--brand-primary)', whiteSpace: 'nowrap' }}>{cfg.action}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
};

export default SubjectsPage;
